import React, { useMemo, useState } from 'react';
import { entriesToCalendarEvents, formatDuration } from './timeTrackerUtils';
import type { TimeTrackerEntry, TimeTrackerProject } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

interface TimeTrackerCalendarViewProps {
  entries: TimeTrackerEntry[];
  projects: TimeTrackerProject[];
  accentColor?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 48;

function getWeekDays(referenceDate: Date): Date[] {
  const day = referenceDate.getDay();
  const monday = new Date(referenceDate);
  monday.setDate(referenceDate.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function formatDay(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export const TimeTrackerCalendarView: React.FC<TimeTrackerCalendarViewProps> = ({
  entries,
  projects,
  accentColor = '#10b981',
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  const events = useMemo(() => entriesToCalendarEvents(entries, projects), [entries, projects]);

  const navigateWeek = (direction: number) => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + direction * 7);
    setCurrentDate(next);
  };

  // Calculate daily totals
  const dailyTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of entries) {
      const key = entry.date;
      totals.set(key, (totals.get(key) || 0) + entry.duration);
    }
    return totals;
  }, [entries]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: `1px solid ${pmTheme.border}`, background: pmTheme.surfaceSubtle }}>
        <button onClick={() => navigateWeek(-1)} style={{ padding: '4px 12px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, background: pmTheme.surface, color: pmTheme.text, cursor: 'pointer', fontSize: 13 }}>
          ← Prev
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: pmTheme.text }}>
            {weekDays[0].toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={() => setCurrentDate(new Date())} style={{ padding: '2px 10px', border: `1px solid ${pmTheme.input}`, borderRadius: 4, background: pmTheme.surface, cursor: 'pointer', fontSize: 12, color: pmTheme.textMuted }}>
            Today
          </button>
        </div>
        <button onClick={() => navigateWeek(1)} style={{ padding: '4px 12px', border: `1px solid ${pmTheme.input}`, borderRadius: 6, background: pmTheme.surface, color: pmTheme.text, cursor: 'pointer', fontSize: 13 }}>
          Next →
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${pmTheme.border}` }}>
        <div style={{ width: 60, flexShrink: 0 }} />
        {weekDays.map((day) => {
          const dateStr = day.toISOString().slice(0, 10);
          const total = dailyTotals.get(dateStr) || 0;
          const isToday = isSameDay(day, new Date());
          return (
            <div
              key={dateStr}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '6px 4px',
                borderLeft: `1px solid ${pmTheme.borderSubtle}`,
                background: isToday ? pmTheme.successSoft : 'transparent',
              }}
            >
              <div style={{ fontSize: 11, color: isToday ? accentColor : pmTheme.textMuted, fontWeight: isToday ? 700 : 400 }}>
                {formatDay(day)}
              </div>
              {total > 0 && (
                <div style={{ fontSize: 10, color: pmTheme.textSubtle, marginTop: 2 }}>
                  {formatDuration(total)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
        <div style={{ display: 'flex', minHeight: HOURS.length * HOUR_HEIGHT }}>
          {/* Hour labels */}
          <div style={{ width: 60, flexShrink: 0 }}>
            {HOURS.map((hour) => (
              <div key={hour} style={{ height: HOUR_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', paddingRight: 8, paddingTop: 2 }}>
                <span style={{ fontSize: 11, color: pmTheme.textSubtle }}>
                  {String(hour).padStart(2, '0')}:00
                </span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day) => {
            const dateStr = day.toISOString().slice(0, 10);
            const dayEvents = events.filter((e) => isSameDay(e.start, day));
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={dateStr}
                style={{
                  flex: 1,
                  borderLeft: `1px solid ${pmTheme.borderSubtle}`,
                  position: 'relative',
                  background: isToday ? pmTheme.successSoft : 'transparent',
                }}
              >
                {/* Hour lines */}
                {HOURS.map((hour) => (
                  <div key={hour} style={{ height: HOUR_HEIGHT, borderBottom: `1px solid ${pmTheme.borderSubtle}` }} />
                ))}

                {/* Events */}
                {dayEvents.map((event) => {
                  const startMinutes = event.start.getHours() * 60 + event.start.getMinutes();
                  const endMinutes = event.end.getHours() * 60 + event.end.getMinutes();
                  const top = (startMinutes / 60) * HOUR_HEIGHT;
                  const height = Math.max(((endMinutes - startMinutes) / 60) * HOUR_HEIGHT, 20);

                  return (
                    <div
                      key={event.id}
                      style={{
                        position: 'absolute',
                        top,
                        left: 2,
                        right: 2,
                        height,
                        background: event.color + '20',
                        borderLeft: `3px solid ${event.color}`,
                        borderRadius: 4,
                        padding: '2px 6px',
                        overflow: 'hidden',
                        fontSize: 11,
                        cursor: 'pointer',
                      }}
                      title={`${event.title} (${event.projectName})`}
                    >
                      <div style={{ fontWeight: 600, color: pmTheme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {event.title}
                      </div>
                      {height > 30 && (
                        <div style={{ color: pmTheme.textMuted, fontSize: 10 }}>{event.projectName}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

TimeTrackerCalendarView.displayName = 'TimeTrackerCalendarView';
